import { Quaternion, Vector3 } from "three";
import { Armature } from "..";

import { JointPositions } from "../../types";
import { Basis, findCentroid } from "../../misc/util";

import { Edge } from "../edge";
import { Joint } from "../joint";
import { clipEdgeLength, Group } from "./group";

// see https://zalo.github.io/blog/kabsch/ for details of algorithm

const tempV = new Vector3();

export class KabschGroup extends Group {
  drives: Joint;
  joints: Joint[] = [];
  targeted: Joint[] = [];
  drivenEdge?: Edge;

  private jointUpdatable: boolean[] = [];

  private maxIters = 1000;

  private quaternion: Quaternion = new Quaternion();

  private tempCurrent: Vector3[] = [];
  private tempTarget: Vector3[] = [];

  private from: Vector3 = new Vector3();
  private to: Vector3 = new Vector3();

  private omega: Vector3 = new Vector3();

  private covariance = [new Vector3(), new Vector3(), new Vector3()];
  private basis = new Basis();

  private tempQ: Quaternion = new Quaternion();

  constructor(drives: Joint, children?: Joint[]) {
    super();

    this.drives = drives;

    let childJoints = drives.childEdges.map((e) => e.end);
    if (children) {
      for (const child of children) {
        this.jointUpdatable.push(childJoints.includes(child));
      }

      childJoints = children;
    } else {
      this.jointUpdatable.push(...childJoints.map((_) => true));
    }

    this.targeted.push(...childJoints);
    this.joints.push(...this.targeted, drives);
    this.jointUpdatable.push(true);

    if (this.jointUpdatable.length != this.joints.length) {
      console.error("invalid kabsch joint list length");
    }

    // label each joint so we can check for membership in solver
    // NB: we don't label the start of the drivenEdge as being in the
    //     KabschGroup, as it belongs to the preceding chain
    for (const joint of this.joints) {
      joint.group = this;
    }

    if (drives.parentEdge) {
      this.drivenEdge = drives.parentEdge;
      this.joints.push(drives.parentEdge.start);
      this.jointUpdatable.push(true);
    }

    // TODO: do we want to do this relative to the waist?
    for (const driven of this.joints) {
      this.tempCurrent.push(driven.pos.clone());
      this.tempTarget.push(driven.pos.clone());
    }

    findCentroid(this.tempCurrent, this.from, this.targeted.length);
  }

  get name(): string {
    return `kabsch: ${this.drives.name}`;
  }

  solveForward(targets: JointPositions): boolean {
    // update vectors with tracking data
    for (let i = 0; i < this.joints.length; i++) {
      if (i < this.targeted.length) {
        const member = this.targeted[i];
        const memberTarget = targets.get(member.name);
        if (memberTarget) {
          this.tempTarget[i].copy(memberTarget);
        } else {
          this.tempTarget[i].copy(member.pos);
        }
      }

      this.tempCurrent[i].copy(this.joints[i].initPos);
    }

    // find optimal translation
    findCentroid(this.tempCurrent, this.from, this.targeted.length);
    findCentroid(this.tempTarget, this.to, this.targeted.length);

    // centralise working points
    this.tempCurrent.forEach((v) => v.sub(this.from));
    this.tempTarget.forEach((v) => v.sub(this.to));

    // determine rotation quaternion
    this.calcCovariance();
    this.polarDecomposition(this.maxIters);

    // apply transform to driven points
    for (let i = 0; i < this.joints.length; i++) {
      if (!this.jointUpdatable[i]) {
        continue;
      }

      const current = this.joints[i];
      this.tempCurrent[i].applyQuaternion(this.quaternion);
      current.pos.addVectors(this.tempCurrent[i], this.to);

      if (i >= this.targeted.length) {
        // TODO: do we want to make targets a map of lists? That way we can
        //       get rid of candidateStartPos
        // if current is the start joint of the driven edge, then we have to
        // handle hubs
        if (
          this.drivenEdge &&
          this.drivenEdge.start == current &&
          current.isHub()
        ) {
          this.drivenEdge.candidateStartPos = current.pos;
        } else {
          const currentTarget = targets.get(current.name);
          if (!currentTarget) {
            targets.set(current.name, new Vector3());
          }
          targets.get(current.name)?.copy(current.pos);
        }
      }

      if (current != this.drivenEdge?.start && current.parentEdge) {
        current.parentEdge.q
          .multiplyQuaternions(this.quaternion, current.parentEdge.qInit)
          .normalize();
        current.parentEdge.updateQuaternions();
      }
    }

    return true;
  }

  solveBackward(_targets: JointPositions): boolean {
    // if (this.drives.name == "_head") return true;

    tempV.copy(this.drives.pos);
    if (this.drivenEdge) {
      clipEdgeLength(this.drivenEdge, true);
      if (this.drivenEdge.hasConstraints()) {
        this.drivenEdge.applyConstraints(true);
      }
    }

    // get position diff and apply to targeted
    tempV.subVectors(this.drives.pos, tempV);

    this.targeted.forEach((j) => {
      j.pos.add(tempV);

      if (j.parentEdge) {
        if (j.parentEdge.hasConstraints()) {
          j.parentEdge.applyConstraints(true);
        } else {
          j.parentEdge.update();
        }
      }
    });

    return true;
  }

  private calcCovariance() {
    // get cross-covariance matrix
    this.covariance.forEach((v) => v.set(0, 0, 0));
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        for (let k = 0; k < this.targeted.length; k++) {
          const v = this.covariance[i];

          v.setComponent(
            j,
            v.getComponent(j) +
              this.tempCurrent[k].getComponent(i) *
                this.tempTarget[k].getComponent(j)
          );
        }
      }
    }
  }

  private polarDecomposition(maxIters: number) {
    const epsilon = 0.000000001;
    this.quaternion.set(0, 0, 0, 1);

    let i = 0;

    for (; i < maxIters; i++) {
      this.basis.setFromQuaternion(this.quaternion);

      const den = Math.abs(
        this.basis.fwd.dot(this.covariance[0]) +
          this.basis.right.dot(this.covariance[1]) +
          this.basis.up.dot(this.covariance[2]) +
          epsilon
      );

      this.basis.fwd.cross(this.covariance[0]);
      this.basis.right.cross(this.covariance[1]);
      this.basis.up.cross(this.covariance[2]);

      this.omega = this.basis.fwd
        .add(this.basis.up)
        .add(this.basis.right)
        .divideScalar(den);

      const w = this.omega.length();
      this.omega.normalize();
      if (w < epsilon) {
        break;
      }

      this.tempQ.setFromAxisAngle(this.omega, w);
      this.quaternion.premultiply(this.tempQ);
      this.quaternion.normalize();
    }
  }
}
